/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DialogService } from '@app/app/shared/helpers/dialog/dialog.service';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import {
  DiagnosticFormattedQuestion,
  DiagnosticQuestionResponse,
  IncludedFeedbackQuestion,
  Organization,
  ReportFeedbackQuestionsSection,
  ReportSection,
  UpdateReportFeedbackQuestionRequest,
} from '@app/app/shared/models';
import { SupportedLanguage } from '@app/app/shared/models/languages/languages.model';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'ptl-feedback-question-report-section',
  templateUrl: './feedback-question-section.component.html',
  styleUrls: ['./feedback-question-section.component.scss'],
})
export class FeedbackQuestionSectionComponent implements OnChanges {
  @Input() section: ReportSection;
  @Input() questions: DiagnosticFormattedQuestion[];
  @Input() currentLanguage: SupportedLanguage;
  @Input() organization: Organization;

  @Output() moveUp = new EventEmitter<void>();
  @Output() sectionDeleted = new EventEmitter<void>();
  @Output() sectionUpdate = new EventEmitter<UpdateReportFeedbackQuestionRequest>();

  isEnabled = false;
  allChecked = true;
  feedbackQuestions: ReportFeedbackQuestionsSection;

  formattedQuestions: IncludedFeedbackQuestion[];

  constructor(
    private dialogService: DialogService,
    private translocoService: TranslocoService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.feedbackQuestions = this.section as ReportFeedbackQuestionsSection;

    this.formattedQuestions = this.getIncludedQuestions(this.questions);
    this.checkIfAllEnabled();
  }

  openDialog(sectionUid: string): void {
    if (!sectionUid) {
      this.sectionDeleted.next();
      return;
    }
    this.dialogService
      .showConfirmDialog(this.translocoService.translate('translations.dialog.title.removeSection'), this.translocoService)
      .then((confirmed) => {
        if (confirmed) {
          this.sectionDeleted.next();
        }
      });
  }

  onAllChecked(): void {
    this.formattedQuestions.map((it) => {
      it.included = this.allChecked;
    });
  }

  save(): void {
    this.sectionUpdate.emit(this.getRequestData());
  }

  saveTitle(title: string): void {
    const req = this.getRequestData();
    req.title = title || undefined;
    this.sectionUpdate.emit(req);
  }

  checkIfAllEnabled(): void {
    if (!this.formattedQuestions) {
      return;
    }
    let allChecked = true;
    for (let i = 0; i < this.formattedQuestions.length; i++) {
      if (!this.formattedQuestions[i].included) {
        allChecked = false;
        break;
      }
    }
    this.allChecked = allChecked;
  }

  private getIncludedQuestions(questions: DiagnosticQuestionResponse[]): IncludedFeedbackQuestion[] {
    if (!questions) {
      return [];
    }
    const data = [];
    questions.map((q) => {
      const includedQuestion = this.feedbackQuestions.includedQuestions.find((question) => question.question.uid === q.uid);

      data.push({
        included: includedQuestion ? includedQuestion.included : true,
        question: {
          ...q,
          instruction: LanguageCodeHelper.getDataByUserLanguageCode(
            q.instructions,
            this.organization,
            this.currentLanguage?.language?.code,
          ),
        },
      });
    });
    return data;
  }

  private getRequestData(): UpdateReportFeedbackQuestionRequest {
    const reqBody: UpdateReportFeedbackQuestionRequest = {
      includedFeedbackQuestions: [],
    };
    this.formattedQuestions.map((it) => {
      reqBody.includedFeedbackQuestions.push({
        included: it.included,
        questionUid: it.question.uid,
      });
    });

    return reqBody;
  }
}
